import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@repo/ui/lib/utils';
import { LoadingMedia } from '../../../../../../../../../apps/web/src/components/loading/default';
import { type CandidateMedia, useScreenSize } from '@repo/ui/hooks';
import { type SpotlightMediaReadyState } from '../component';

type ImageProps = {
  enableDesktopZoom: boolean;
  media: CandidateMedia;
  nextMedia?: CandidateMedia;
  imageMediaAlt: string;
  onImageAnimationEnd: () => void;
  onReady: (
    data: Omit<SpotlightMediaReadyState, 'disposeVideo' | 'pauseVideo'>,
  ) => void;
};

export const Image = ({
  media,
  nextMedia,
  enableDesktopZoom: initialEnableDesktopZoom,
  onReady,
  imageMediaAlt,
  onImageAnimationEnd,
}: ImageProps) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [startAnimation, setStartAnimation] = useState(false);
  const [zoomCoordinates, setZoomCoordinates] = useState({ x: 0, y: 0 });
  const { isSmall } = useScreenSize();

  const enableDesktopZoom = useMemo(
    () => initialEnableDesktopZoom && !isSmall,
    [initialEnableDesktopZoom, isSmall],
  );
  const imageMediaFigureStyles = useMemo(() => {
    if (enableDesktopZoom) {
      return {
        backgroundImage: `url('${media?.url}')`,
        backgroundPosition: `${zoomCoordinates.x}% ${zoomCoordinates.y}%`,
      };
    }

    return {};
  }, [enableDesktopZoom, media?.url, zoomCoordinates]);

  const handleImageMediaMouseMove = (event: MouseEvent<HTMLElement>) => {
    const { offsetX, offsetY } = event.nativeEvent;
    const x = (offsetX / event.currentTarget.offsetWidth) * 100;
    const y = (offsetY / event.currentTarget.offsetHeight) * 100;

    setZoomCoordinates({ x, y });
  };

  useEffect(() => {
    onReady?.({
      startImageMediaAnimation: () => {
        if (nextMedia) {
          setStartAnimation(true);
        }
      },
    });
  }, []);

  useEffect(() => {
    const animationEndEventListener = () => {
      onImageAnimationEnd?.();
      setStartAnimation(false);
    };
    imageRef?.current?.addEventListener(
      'animationend',
      animationEndEventListener,
    );

    return () =>
      imageRef?.current?.removeEventListener(
        'animationend',
        animationEndEventListener,
      );
  }, [imageRef?.current, nextMedia, onImageAnimationEnd]);

  return (
    <>
      <figure
        className={cn(
          'w-full h-full relative object-contain aspect-[322/429]',
          {
            'group/spotlight-image hover:cursor-zoom-in overflow-hidden':
              enableDesktopZoom,
          },
        )}
        onMouseMove={enableDesktopZoom ? handleImageMediaMouseMove : undefined}
        style={imageMediaFigureStyles}
      >
        {nextMedia && (
          <img
            src={nextMedia.url}
            alt={imageMediaAlt}
            className={cn(
              'absolute inset-x-0 inset-y-0 w-full h-full object-contain',
              {
                'group-hover/spotlight-image:opacity-0': enableDesktopZoom,
              },
            )}
          />
        )}
        <img
          ref={imageRef}
          src={media?.url}
          draggable={false}
          alt={imageMediaAlt}
          className={cn('w-full h-full object-contain relative z-10', {
            'transition duration-200 opacity-100 group-hover/spotlight-image:opacity-0':
              enableDesktopZoom,
            'animate-fade-out': startAnimation,
          })}
        />
      </figure>
      {media.isProcessing && <LoadingMedia type="skeleton" />}
    </>
  );
};
